// src/services/affiliateService.js
//
// Ponte do app com o programa de afiliados. ISOLADO de propósito: nenhuma tela
// do redesign precisa saber que isto existe, e o dia em que o programa mudar,
// muda só aqui.
//
// O app NÃO decide nada sobre o programa. Ele:
//   • guarda o código de indicação que chegou pelo link (antes do cadastro);
//   • avisa o backend quando o usuário conclui o onboarding e entra no app.
//
// Quem valida, conta e concede é o backend — inclusive relendo o Firestore
// para confirmar o onboarding. Se o app pudesse marcar `qualified = true`,
// bastaria um POST para fabricar indicações e destravar Premium de graça.

import { Capacitor } from "@capacitor/core";
import { auth } from "../firebaseConfig";
import { API_BASE_URL } from "../config/api";

/** Mesma chave que `useAffiliateTracker` já usa há tempos. Não renomear: há
 *  visitantes com o código guardado no navegador agora. */
export const AFFILIATE_STORAGE_KEY = "affiliateData";

/** Marca local de "já avisei o backend" — evita uma chamada por render. O
 *  backend é idempotente de qualquer forma; isto só poupa rede. */
const QUALIFY_MARK_PREFIX = "lesvita:afiliado:qualificado:";

/** Dados de indicação guardados no navegador, ou null. */
export function getStoredAffiliate() {
  try {
    const raw = localStorage.getItem(AFFILIATE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getStoredAffiliateCode() {
  return (getStoredAffiliate()?.AFFILIATE_CODE || "").toUpperCase();
}

/**
 * Plataforma real de execução. Só telemetria: a identidade que vale é o
 * usuário autenticado, nunca o device. Nada de fingerprint.
 */
export function currentPlatform() {
  try {
    return Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web";
  } catch {
    return "web";
  }
}

async function authedFetch(path, body) {
  const user = auth.currentUser;
  if (!user) throw new Error("sem sessão");
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE_URL}/afiliados${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
  });
  return res.json().catch(() => ({}));
}

/**
 * Avisa que este usuário concluiu o onboarding e chegou na experiência
 * principal — o gatilho da indicação qualificada.
 *
 * O uid NÃO viaja: o backend o tira do ID token. Não existe forma de este
 * app qualificar outra pessoa.
 */
export async function signalQualification() {
  return authedFetch("/indicacao/qualificar", { plataforma: currentPlatform() });
}

/**
 * Vincula o usuário autenticado a um cupom (caminho em tempo real, opcional).
 * O caminho principal continua sendo o `AFFILIATE_CODE` que o `verifyCode`
 * grava no cadastro e o backend sincroniza.
 */
export async function attachReferral(codigo) {
  if (!codigo) return { status: "sem_codigo" };
  return authedFetch("/indicacao", { codigo, origem: "APP" });
}

// ── Marca local (só para não repetir chamada à toa) ─────────────────────────

export function alreadySignaled(uid) {
  try {
    return localStorage.getItem(QUALIFY_MARK_PREFIX + uid) === "1";
  } catch {
    return false;
  }
}

export function markSignaled(uid) {
  try {
    localStorage.setItem(QUALIFY_MARK_PREFIX + uid, "1");
  } catch {
    /* navegador sem storage: a chamada repete, e o backend dedupe. */
  }
}
